/**
 * Describe one field (a property definition): its label, key, type,
 * options, group, whether it is required and a description.
 *
 * The key is made from the label as it is typed and can be changed until
 * the field is saved; after that it is fixed, because stored values are
 * found by it. A field already seen on leads opens with its key filled in,
 * so describing it labels the data leads already carry. A lead field cannot
 * be required: leads are read as they arrive, so there is nothing to
 * enforce it on.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Autocomplete, Button, ColorSwatch, Drawer, Group, Select, Stack, Switch, Text, TextInput,
  Textarea, Tooltip,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  usePropertyEdits, type PropertyDef, type PropertyObject, type PropertyOption, type PropertyType,
} from '../../api/properties';
import { FONTS } from '../../theme';
import { useVocab } from '../../vocab';
import { UsedByPanel } from '../workflows/UsedByPanel';
import { hasOptions, objectWord, PROPERTY_TYPES, TYPE_LABEL } from './propertyModel';

const COLOURS = ['gray', 'red', 'pink', 'grape', 'violet', 'indigo', 'blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange'];

/** A key from a label: lower case, words joined by underscores. */
export function keyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

type Draft = {
  label: string;
  key: string;
  keyTouched: boolean;
  type: PropertyType;
  options: PropertyOption[];
  group: string;
  required: boolean;
  description: string;
};

/** "boiler_age" as "Boiler age": a first guess at a seen key's label. */
export function labelFromKey(key: string): string {
  const words = key.replace(/[_\s]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

function draftFrom(def: PropertyDef | null, initialKey?: string | null): Draft {
  return {
    label: def?.label ?? (initialKey ? labelFromKey(initialKey) : ''),
    key: def?.key ?? initialKey ?? '',
    keyTouched: !!def || !!initialKey,
    type: def?.type ?? 'text',
    options: def?.options.map((o) => ({ ...o })) ?? [],
    group: def?.group_name ?? '',
    required: def?.required ?? false,
    description: def?.description ?? '',
  };
}

export function PropertyDrawer({
  opened, object, def, initialKey, groups, onClose,
}: {
  opened: boolean;
  object: PropertyObject;
  /** The field being edited; null to describe a new one. */
  def: PropertyDef | null;
  /** A key already seen on leads, to describe it rather than make a new one. */
  initialKey?: string | null;
  /** Group names already in use, offered as you type. */
  groups: string[];
  onClose: () => void;
}) {
  const v = useVocab();
  const { create, update } = usePropertyEdits();
  const [d, setD] = useState<Draft>(() => draftFrom(def, initialKey));
  const [error, setError] = useState('');

  useEffect(() => {
    if (opened) {
      setD(draftFrom(def, initialKey));
      setError('');
    }
  }, [opened, def, initialKey]);

  const editing = !!def;
  const keyOk = /^[A-Za-z0-9_]{1,64}$/.test(d.key);
  const optionsNeeded = hasOptions(d.type);
  const optionValues = d.options.map((o) => o.value.trim().toLowerCase());
  const optionsOk = !optionsNeeded
    || (d.options.length > 0 && optionValues.every((x) => x) && new Set(optionValues).size === optionValues.length);
  const ready = d.label.trim() !== '' && keyOk && optionsOk;

  const setOption = (i: number, patch: Partial<PropertyOption>) =>
    setD({ ...d, options: d.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const moveOption = (i: number, by: number) => {
    const next = [...d.options];
    const [o] = next.splice(i, 1);
    if (o) next.splice(i + by, 0, o);
    setD({ ...d, options: next });
  };

  async function save() {
    setError('');
    const options = optionsNeeded
      ? d.options.map((o) => ({
        value: o.value.trim(),
        label: (o.label ?? '').trim() || o.value.trim(),
        ...(o.colour ? { colour: o.colour } : {}),
      }))
      : [];
    try {
      if (def) {
        await update.mutateAsync({
          id: def.id,
          body: {
            label: d.label.trim(),
            type: d.type,
            options,
            group_name: d.group.trim(),
            required: d.required,
            description: d.description.trim(),
          },
        });
      } else {
        await create.mutateAsync({
          object_type: object,
          key: d.key,
          label: d.label.trim(),
          type: d.type,
          options,
          group_name: d.group.trim(),
          required: d.required,
          description: d.description.trim(),
        });
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={editing
        ? `Edit ${def.label}`
        : initialKey
          ? 'Add a label and type'
          : `New ${v.one('property')} on ${objectWord(v, object).toLowerCase()}`}
      styles={{ title: { fontSize: 16, fontWeight: 600 } }}
    >
      <Stack data-testid="property-drawer">
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput
          label="Label"
          required
          data-autofocus
          value={d.label}
          onChange={(e) => {
            const label = e.currentTarget.value;
            setD({ ...d, label, key: d.keyTouched ? d.key : keyFromLabel(label) });
          }}
        />
        <TextInput
          label="Key"
          description={editing
            ? 'Fixed once created: stored values are found by it.'
            : `How it is stored and searched: custom.${d.key || 'key'}. Letters, digits and underscores. Fixed once created.`}
          disabled={editing}
          value={d.key}
          error={d.key && !keyOk ? 'Letters, digits and underscores only' : undefined}
          onChange={(e) => setD({ ...d, key: e.currentTarget.value, keyTouched: true })}
          styles={{ input: { fontFamily: FONTS.mono } }}
        />
        <Select
          label="Type"
          allowDeselect={false}
          data={PROPERTY_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
          value={d.type}
          onChange={(t) => t && setD({ ...d, type: t as PropertyType })}
        />
        {editing && def.type !== d.type && (
          <Alert variant="light" color="chartAmber">
            <Text size="sm">Values already stored keep what they hold. They are checked against the new type the next time they are changed.</Text>
          </Alert>
        )}
        {optionsNeeded && (
          <Stack gap={6} data-testid="options-editor">
            <Group justify="space-between">
              <Text size="sm" fw={500}>Options</Text>
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconPlus size={12} />}
                onClick={() => setD({ ...d, options: [...d.options, { value: '', label: '' }] })}
              >
                Add option
              </Button>
            </Group>
            {d.options.length === 0 && <Text size="xs" c="dimmed">Add at least one.</Text>}
            {d.options.map((o, i) => (
              <Group key={i} gap={6} wrap="nowrap" align="center">
                <TextInput
                  size="xs"
                  aria-label={`Option ${i + 1} value`}
                  placeholder="Stored value"
                  value={o.value}
                  onChange={(e) => setOption(i, { value: e.currentTarget.value })}
                  style={{ flex: 1 }}
                  styles={{ input: { fontFamily: FONTS.mono } }}
                />
                <TextInput
                  size="xs"
                  aria-label={`Option ${i + 1} label`}
                  placeholder="Shown as"
                  value={o.label ?? ''}
                  onChange={(e) => setOption(i, { label: e.currentTarget.value })}
                  style={{ flex: 1 }}
                />
                <Select
                  size="xs"
                  aria-label={`Option ${i + 1} colour`}
                  placeholder="Colour"
                  clearable
                  w={96}
                  data={COLOURS.map((c) => ({ value: c, label: c }))}
                  value={o.colour ?? null}
                  leftSection={o.colour ? <ColorSwatch size={10} color={`var(--mantine-color-${o.colour}-6)`} /> : undefined}
                  onChange={(c) => setOption(i, { colour: c ?? undefined })}
                  renderOption={({ option }) => (
                    <Group gap={6}><ColorSwatch size={10} color={`var(--mantine-color-${option.value}-6)`} />{option.label}</Group>
                  )}
                />
                <ActionIcon variant="subtle" size="sm" aria-label="Move up" disabled={i === 0} onClick={() => moveOption(i, -1)}>
                  <IconArrowUp size={13} />
                </ActionIcon>
                <ActionIcon variant="subtle" size="sm" aria-label="Move down" disabled={i === d.options.length - 1} onClick={() => moveOption(i, 1)}>
                  <IconArrowDown size={13} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  aria-label="Remove option"
                  onClick={() => setD({ ...d, options: d.options.filter((_, j) => j !== i) })}
                >
                  <IconTrash size={13} />
                </ActionIcon>
              </Group>
            ))}
            {!optionsOk && d.options.length > 0 && (
              <Text size="xs" c="red">Every option needs a value, and no two the same.</Text>
            )}
          </Stack>
        )}
        <Autocomplete
          label="Group"
          description="The heading it is shown under. Leave empty for Other."
          data={groups}
          value={d.group}
          onChange={(g) => setD({ ...d, group: g })}
        />
        <Tooltip
          label={`${v.Many('lead')} are read as they arrive, so a ${v.one('lead')} field cannot be required.`}
          disabled={object !== 'submission'}
          withArrow
        >
          <div>
            <Switch
              label="Required"
              description="Needed when a record is created. Existing records are not affected."
              checked={d.required}
              disabled={object === 'submission'}
              onChange={(e) => setD({ ...d, required: e.currentTarget.checked })}
            />
          </div>
        </Tooltip>
        <Textarea
          label="Description"
          description="Shown under the field when it is edited."
          autosize
          minRows={2}
          value={d.description}
          onChange={(e) => setD({ ...d, description: e.currentTarget.value })}
        />
        {editing && object !== 'submission' && (
          <UsedByPanel
            refType="property"
            refId={`${object}:${def.key}`}
            title={`${v.Many('workflow')} that use it`}
            testId="property-used-by"
          />
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready} loading={create.isPending || update.isPending} onClick={save}>
            {editing || initialKey ? 'Save' : `Create ${v.one('property')}`}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
